import { useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { useMMKVObject } from 'react-native-mmkv';
import { z } from 'zod/v4';

import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import {
  type ConfirmOrderFormValues,
  createConfirmOrderFormSchema,
  createUserInformationSchema,
} from '@/components/_shared/UserInformationForm/schema';
import type { GuestUserInfo } from '@/components/_shared/UserInformationForm/types';
import { storage, storageKeys } from '@/constants/storage';
import { useCustomerQuery } from '@/hooks/queries/customer/useCustomerQuery';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { useTranslatedForm } from '@/hooks/useTranslatedForm';
import type { PaymentMethod } from '@/utils/paymentMethods';

import type { UseConfirmOrderFormParams } from './types';

export const useConfirmOrderForm = ({
  onSubmit,
  onSubmitError,
  onDismiss,
  requirePaymentMethod = true,
}: UseConfirmOrderFormParams) => {
  const bottomSheetRef = useRef<BottomSheetRef>(null);
  const {
    data: customer,
    isFetching: isCustomerFetching,
    customerId,
  } = useCustomerQuery();

  // True only during the post-auth settle: a customerId was just written (e.g.
  // by social sign-in) and the customer query is fetching but not yet resolved.
  // Guests have no customerId (query disabled → not fetching) so this is false.
  const isCustomerSettling = !!customerId && isCustomerFetching && !customer;

  const [guestUserInfo] = useMMKVObject<GuestUserInfo>(
    storageKeys.guestUserInfo,
    storage,
  );

  const { t } = useScreenTranslations(
    'ConfirmBookingBottomSheet.userInformation.validation',
  );

  const schema = useMemo(
    () =>
      requirePaymentMethod
        ? createConfirmOrderFormSchema(t)
        : createUserInformationSchema(t).extend({
            paymentMethod: z.custom<PaymentMethod>().optional(),
          }),
    [t, requirePaymentMethod],
  );

  const form = useTranslatedForm({
    fieldsNs: 'ConfirmBookingBottomSheet.userInformation.validation',
    schema,
    mode: 'onChange',
    defaultValues: {
      phone: customer?.phone || guestUserInfo?.phone || '',
      email: customer?.email || guestUserInfo?.email || '',
      lastName: customer?.lastName || guestUserInfo?.lastName || '',
      firstName: customer?.firstName || guestUserInfo?.firstName || '',
      paymentMethod: undefined as PaymentMethod | undefined,
    },
  });

  const selectedPaymentMethod = useWatch({
    control: form.control,
    name: 'paymentMethod',
  });

  const isFormValid = form.formState.isValid;

  const invalidUserInformation =
    selectedPaymentMethod !== undefined &&
    Boolean(
      form.formState.errors.firstName ||
        form.formState.errors.lastName ||
        form.formState.errors.phone ||
        form.formState.errors.email,
    );

  useEffect(() => {
    if (customer) {
      form.reset({
        phone: customer.phone || '',
        email: customer.email || '',
        lastName: customer.lastName || '',
        firstName: customer.firstName || '',
        paymentMethod: selectedPaymentMethod,
      });
    }
    // Only re-run when customer changes (e.g. guest signs in). Including form
    // or selectedPaymentMethod would cause infinite loops since form.reset()
    // updates both, triggering the effect again.
  }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // For guests, re-populate the form when guestUserInfo becomes available
    // (e.g. returning from a failed BNPL payment, which persists it on "Book").
    // defaultValues only apply at mount, so an already-mounted form needs an
    // explicit reset. guestUserInfo only ever changes when the user taps "Book"
    // (storing their own current input), so resetting from it never clobbers
    // unsaved edits — no isDirty guard needed. Skip only for logged-in users,
    // whose form is driven by the customer record (handled above).
    if (customer || !guestUserInfo) return;
    form.reset({
      phone: guestUserInfo.phone || '',
      email: guestUserInfo.email || '',
      lastName: guestUserInfo.lastName || '',
      firstName: guestUserInfo.firstName || '',
      paymentMethod: selectedPaymentMethod,
    });
    // Re-run when guestUserInfo changes. form/selectedPaymentMethod are omitted
    // to avoid reset() → re-render → reset() loops, matching the effect above.
  }, [guestUserInfo, customer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedPaymentMethod && !!customer) {
      form.trigger(['firstName', 'lastName', 'phone', 'email'], {
        shouldFocus: true,
      });
    }
  }, [selectedPaymentMethod, form, customer]);

  const handleSubmit = form.handleSubmit(
    (data) => onSubmit(data as ConfirmOrderFormValues),
    onSubmitError,
  );

  const handleDismiss = () => {
    form.reset();
    onDismiss?.();
  };

  const closeBottomSheet = () => {
    bottomSheetRef.current?.close();
  };

  return {
    form,
    selectedPaymentMethod,
    isFormValid,
    invalidUserInformation,
    handleSubmit,
    handleDismiss,
    closeBottomSheet,
    bottomSheetRef,
    customer: customer ?? undefined,
    isCustomerSettling,
  };
};
